



































import { FrontendChannel } from '@/@types/types';
import AppChannelList from '@/components/channels/ChannelList.vue';
import { ChannelType } from '@/components/shared/types';
import { filterHiddenChannels, getMediaRoomUrl } from '@/helpers/channel-helper';
import { Channel, Configuration } from '@linkbox/sdk';
import { defineComponent, PropType } from '@vue/composition-api';
import * as _ from 'lodash';
import { createNamespacedHelpers, mapActions, mapState } from 'vuex';
import AppTransitionExpand from '../shared/TransitionExpand.vue';
import AppSelectedChannel from './SelectedChannel.vue';

const audioConsumer = createNamespacedHelpers('mediaSoupConnection/audioConsumer');
const auth = createNamespacedHelpers('auth');
const notifications = createNamespacedHelpers('notifications');
const systemInformation = createNamespacedHelpers('systemInformation');

const nullSaveNumber = (number) => (number ? parseInt(number) : 0);

export default defineComponent({
  name: 'AppChannels',
  components: { AppChannelList, AppSelectedChannel, AppTransitionExpand },
  props: {
    channels: Array,
    configuration: Object as PropType<Configuration>,
    loading: Boolean,
  },
  methods: {
    ...mapActions({ selectChannel: 'selectChannel' }),
    ...audioConsumer.mapActions({ togglePlay: 'togglePlay', pause: 'pause' }),
    ...notifications.mapActions({ showError: 'showError' }),
    onChannelUnselect() {
      this.$mediaSoup.disconnect();
      this.pause();
      this.selectChannel(null);
    },
    async onChannelSelect(channel: FrontendChannel) {
      if (!(await this.$mediaSoup.canConnect())) {
        this.showError({ title: 'Too many listeners', description: 'Try again or ask your event operator for help.' });
        return;
      }
      await this.$mediaSoup.disconnect();
      this.selectChannel(channel);
      const mediaRoomUrl = getMediaRoomUrl(channel);
      if (mediaRoomUrl) {
        await this.$mediaSoup.connectAndConsume(mediaRoomUrl, channel.id);
      }
    },
  },
  watch: {
    async channels(channels) {
      if (this.selectedChannel) {
        const updatedChannel = _.find(filterHiddenChannels(channels, this.isLoggedIn), { id: this.selectedChannel.id });

        if (!updatedChannel || !_.get(updatedChannel, 'enabled', false)) {
          this.onChannelUnselect();
          return;
        }

        const updatedChannelWithDelayInfo = {
          ...updatedChannel,
          cloudDelay: this.selectedChannel.cloudDelay,
        };

        const selectedChannelRoomUrl = getMediaRoomUrl(this.selectedChannel);
        const updatedChannelRoomUrl = getMediaRoomUrl(updatedChannelWithDelayInfo);
        if (selectedChannelRoomUrl !== updatedChannelRoomUrl) {
          this.onChannelSelect(updatedChannelWithDelayInfo);
        }
      }
    },
  },
  computed: {
    ...mapState({
      selectedChannel: 'selectedChannel',
    }),
    ...audioConsumer.mapState({
      mergedStream: 'mergedStream',
      audioConsumer: 'audioConsumer',
    }),
    ...systemInformation.mapState({ tenantId: 'tenantId' }),
    ...systemInformation.mapGetters({ isCloudDeployment: 'isCloudDeployment' }),
    ...auth.mapGetters({
      isLoggedIn: 'isLoggedIn',
    }),
    showSelectedChannel(): Channel {
      return this.selectedChannel;
    },
    visibleAudiocastChannels(): Channel[] {
      return _.filter(filterHiddenChannels(this.channels, this.isLoggedIn), {
        __typename: ChannelType.LISTEN_ONLY_CHANNEL,
      });
    },
    title(): string | undefined | null {
      return this.configuration?.title;
    },
    channelsWithCloudDelay(): FrontendChannel[] {
      if (!this.isCloudDeployment) {
        return [];
      } else {
        return this.visibleAudiocastChannels
          .filter((channel: Channel) => {
            return (
              channel?.source?.__typename === 'PortableSource' && nullSaveNumber(channel?.source?.externalDelay) !== 0
            );
          })
          .map((channel) => ({ ...channel, cloudDelay: true }));
      }
    },
  },
});
